import type { LiveExtension, LiveQueueAgent } from '@arcnet/contracts';
import { Badge } from '../../components/ui/Primitives.js';
import { formatDuration } from '../../lib/format.js';
import { isUnreachable } from '../extensions/ExtensionPresence.js';

/**
 * A queue's agents, live (owner, 30 September 2026: "why cant i see the currently configured queues
 * with the agents/extensions in them? even if they are offline?"). Every agent in the queue is
 * listed, logged out or not, in the order calls are offered to them.
 */

/** What the roster needs of a seat: its name, and whether its phone can ring. */
export interface RosterSeat {
  display_name: string;
  registered: boolean;
  ping_status?: string | null;
}

type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

/**
 * One state per agent, in the order a supervisor asks: are they even on, are they busy, will
 * they be free soon, can they take the next call. The switch's status and state are two answers
 * to different questions; this is the one the page shows.
 */
export function agentState(a: LiveQueueAgent, now: number, ext: LiveExtension | undefined): {
  label: string; tone: Tone; secs: number | null; free: boolean;
} {
  const since = (ms: number | null) => (ms === null ? null : Math.max(0, Math.floor((now - ms) / 1000)));
  if (a.status === 'Logged Out') return { label: 'Logged out', tone: 'muted', secs: null, free: false };
  if (a.status === 'On Break') return { label: 'On a break', tone: 'warn', secs: since(a.since), free: false };
  if (a.state === 'In a queue call') return { label: 'On a queue call', tone: 'info', secs: since(a.since), free: false };
  if (a.state === 'Receiving') return { label: 'Ringing', tone: 'info', secs: null, free: false };
  if (a.ready_at !== null && a.ready_at > now) {
    return { label: 'Wrap-up', tone: 'warn', secs: Math.ceil((a.ready_at - now) / 1000), free: false };
  }
  if (ext?.state === 'on_call') return { label: 'On another call', tone: 'info', secs: since(ext.since), free: false };
  return { label: 'Ready', tone: 'ok', secs: null, free: true };
}

export function QueueRoster({ agents, domain, seats, calls, now }: {
  agents: LiveQueueAgent[];
  domain: string;
  seats: ReadonlyMap<string, RosterSeat>;
  calls: ReadonlyMap<string, LiveExtension>;
  now: number;
}) {
  if (agents.length === 0) {
    return <p className="arc-roster-empty">No agents in this queue. Add them under Call routing › Queues.</p>;
  }
  const tiers = new Set(agents.map((a) => a.level)).size > 1;
  return (
    <ul className="arc-roster" aria-label="Agents">
      {agents.map((a) => {
        const key = `${a.agent}@${domain}`;
        const seat = seats.get(key);
        const s = agentState(a, now, calls.get(key));
        // A phone that cannot ring matters most for an agent the queue will offer calls to.
        const phoneDown = seat !== undefined && (!seat.registered || isUnreachable(seat.ping_status));
        return (
          <li key={a.agent} className="arc-roster-item">
            <span className={`arc-roster-dot is-${s.tone}`} aria-hidden="true" />
            <span className="arc-mono arc-roster-num">{a.agent}</span>
            <span className="arc-truncate arc-roster-name">
              {seat?.display_name ?? <span className="arc-subtle">unknown extension</span>}
              {tiers && <span className="arc-subtle"> · tier {a.level}</span>}
            </span>
            <span className="arc-roster-state">
              {phoneDown && a.status !== 'Logged Out' && (
                <Badge tone={s.free ? 'danger' : 'warn'}>phone offline</Badge>
              )}
              <span style={{ color: s.tone === 'muted' ? 'var(--arc-fg-3)' : `var(--arc-${s.tone})`, fontWeight: 550 }}>
                {s.label}
              </span>
              {s.secs !== null && (
                <span className="arc-subtle arc-num">
                  {s.label === 'Wrap-up' ? `${s.secs}s left` : formatDuration(s.secs)}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
